import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import axios from '../utils/axiosConfig';
import ImagePreview from './ImagePreview';
import Loader from './Loader';
import { useFileAttachments } from '../hooks/useFileAttachments.js';
import { revokeAttachmentPreview } from '../utils/attachmentUtils.js';

// Constants
const MAX_ATTACHMENTS = 10;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Formats file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let index = 0;
  let value = bytes;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  const precision = value >= 10 || index === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[index]}`;
};

/**
 * Maps API file types to browser-compatible MIME types
 * @param {string} apiType - API file type (e.g., "file/pdf", "image/png")
 * @returns {string} Browser MIME type (e.g., "application/pdf", "image/png")
 */
const convertApiTypeToMimeType = (apiType) => {
  if (!apiType) return null;

  const mimeTypeMap = {
    'file/pdf': 'application/pdf',
    'image/png': 'image/png',
    'image/jpeg': 'image/jpeg',
    'image/jpg': 'image/jpeg', // jpg maps to jpeg MIME type
  };

  const normalized = apiType.toLowerCase();
  return mimeTypeMap[normalized] || normalized;
};

/**
 * Gets file extension from filename
 * @param {string} filename - File name
 * @returns {string} File extension in lowercase (e.g., "pdf", "png")
 */
const getFileExtension = (filename) => {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

/**
 * Checks if a file type matches allowed types
 * @param {File} file - File object
 * @param {Array<string>} allowedApiTypes - Array of API file types (e.g., ["file/pdf", "image/png"])
 * @returns {boolean} True if file type is allowed
 */
const isFileTypeAllowed = (file, allowedApiTypes) => {
  if (!Array.isArray(allowedApiTypes) || allowedApiTypes.length === 0) {
    return true; // If no restrictions, allow all
  }

  const fileMimeType = file.type?.toLowerCase();
  const fileExtension = getFileExtension(file.name);

  // Convert API types to browser MIME types
  const allowedMimeTypes = allowedApiTypes
    .map(convertApiTypeToMimeType)
    .filter(Boolean);

  // Check MIME type match
  if (fileMimeType && allowedMimeTypes.includes(fileMimeType)) {
    return true;
  }

  // Fallback: check file extension
  const extensionToApiTypeMap = {
    'pdf': 'file/pdf',
    'png': 'image/png',
    'jpeg': 'image/jpeg',
    'jpg': 'image/jpg',
  };

  if (fileExtension && extensionToApiTypeMap[fileExtension]) {
    const apiTypeForExtension = extensionToApiTypeMap[fileExtension];
    if (allowedApiTypes.map(t => t?.toLowerCase()).includes(apiTypeForExtension)) {
      return true;
    }
  }

  return false;
};

/**
 * Converts API file types to browser-compatible MIME types for accept attribute
 * @param {Array<string>} fileTypes - Array of file types from API (e.g., ["file/pdf", "image/png"])
 * @returns {string} Comma-separated string of browser-compatible MIME types
 */
const convertToBrowserAcceptTypes = (fileTypes = []) => {
  if (!Array.isArray(fileTypes) || fileTypes.length === 0) {
    return undefined;
  }

  const browserTypes = fileTypes
    .map(convertApiTypeToMimeType)
    .filter(Boolean);

  return browserTypes.length > 0 ? browserTypes.join(',') : undefined;
};

const FileAttachments = ({
  isIntialLanguageSelected,
  maxAttachments = MAX_ATTACHMENTS,
  maxFileSizeMB = MAX_FILE_SIZE_MB,
  primary,
}) => {
  const { t } = useTranslation();
  const {
    attachments = [],
    isUploadingFiles = false,
    fileSizeLimitBytes,
    allowedFileTypes = [],
    attachmentsUploadResponse,
    orgId,
    handleAttachmentsAdd: onAttachmentsAdd,
    handleAttachmentRemove: onAttachmentRemove,
    registerAttachmentTrigger,
  } = useFileAttachments();
  const maxFileSizeBytes = fileSizeLimitBytes || (maxFileSizeMB * 1024 * 1024);
  const fileInputRef = useRef(null);
  const [previewedImage, setPreviewedImage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [wasUploading, setWasUploading] = useState(false);

  // Handle cleanup of failed uploads
  useEffect(() => {
    if (isUploadingFiles) {
      setWasUploading(true);
    } else if (wasUploading && !isUploadingFiles) {
      // Upload just finished, check for any attachments that still have temp IDs
      const uploadedFileIds = new Set(
        (attachmentsUploadResponse?.data || [])
          .map(file => file.id)
          .filter(Boolean)
      );

      attachments.forEach(attachment => {
        const isTempId = typeof attachment.id === 'string' && attachment.id.includes('-') && attachment.id.split('-').length >= 4;
        if (isTempId && !uploadedFileIds.has(attachment.id)) {
          revokeAttachmentPreview(attachment);
          onAttachmentRemove(attachment.id);
        }
      });
      setWasUploading(false);
    }
  }, [isUploadingFiles, attachments, attachmentsUploadResponse, onAttachmentRemove, wasUploading]);

  // Triggers the file input dialog
  const triggerFileDialog = useCallback(() => {
    if (!isIntialLanguageSelected) return;
    if (attachments?.length >= maxAttachments) {
      toast.error('You may only upload 10 files at a time.');
      return;
    }
    fileInputRef.current?.click();
  }, [isIntialLanguageSelected, attachments, maxAttachments]);

  // Registers the file dialog trigger function
  useEffect(() => {
    if (typeof registerAttachmentTrigger === 'function') {
      registerAttachmentTrigger(triggerFileDialog);
    }

    return () => {
      if (typeof registerAttachmentTrigger === 'function') {
        registerAttachmentTrigger(null);
      }
    };
  }, [registerAttachmentTrigger, triggerFileDialog]);

  /**
   * Processes and adds files to attachments
   * @param {File[]} files - Array of File objects
   */
  const processFiles = (files = []) => {
    if (!files.length) return;

    const currentCount = attachments.length;
    const remainingSlots = maxAttachments - currentCount;

    if (remainingSlots <= 0) {
      toast.error('You may only upload 10 files at a time.');
      return;
    }

    const hydratedFiles = [];
    let filesAdded = 0;
    const effectiveMaxBytes = maxFileSizeBytes || (maxFileSizeMB * 1024 * 1024);
    const maxSizeMB = effectiveMaxBytes / (1024 * 1024);
    const normalizedAllowedTypes = Array.isArray(allowedFileTypes)
      ? allowedFileTypes.map((t) => t?.toLowerCase()).filter(Boolean)
      : [];
    files.forEach((file) => {
      if (filesAdded >= remainingSlots) {
        toast.error('You may only upload 10 files at a time.');
        return;
      }

      if (effectiveMaxBytes && file.size > effectiveMaxBytes) {
        const displayLimit = maxSizeMB >= 10 ? Math.round(maxSizeMB) : parseFloat(maxSizeMB.toFixed(1));
        toast.error(`${file.name} exceeds ${displayLimit}MB limit`);
        return;
      }

      // File type validation based ONLY on allowedFileTypes from bot config
      if (normalizedAllowedTypes.length) {
        const isAllowed = isFileTypeAllowed(file, normalizedAllowedTypes);

        if (!isAllowed) {
          const allowedHumanReadable = normalizedAllowedTypes.join(', ');
          toast.error(`${file.name} is not an allowed file type. Allowed types: ${allowedHumanReadable}`);
          return;
        }
      }

      const id = `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const isImage = file.type?.startsWith('image/');

      hydratedFiles.push({
        id,
        file,
        name: file.name,
        size: file.size,
        readableSize: formatFileSize(file.size),
        type: file.type,
        isImage,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
        extension: file.name?.split('.').pop()?.toUpperCase() || '',
      });

      filesAdded++;
    });

    if (!hydratedFiles.length) return;

    return hydratedFiles;
  };

  /**
   * Handles file selection from input
   * @param {Event} event - File input change event
   */
  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files || []);

    if (files?.length && typeof onAttachmentsAdd === 'function') {
      if (attachments?.length >= maxAttachments) {
        toast.error('You may only upload 10 files at a time.');
        event.target.value = '';
        return;
      }

      const processedFiles = processFiles(files);
      if (processedFiles && processedFiles.length > 0) {
        onAttachmentsAdd(processedFiles);
      }
    }

    event.target.value = '';
  };

  /**
   * Deletes file from server via API
   * @param {Object} attachment - Attachment object with id
   */
  const deleteFileFromServer = async (attachment) => {
    try {
      setIsDeleting(true);
      const apiKey = process.env.REACT_APP_BOT_API_KEY;
      const mediaApiURL = process.env.REACT_APP_MEDIA_API_URL || "https://media.brainstormer.dev";
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      let botId = urlParams?.get("bot");

      if (botId && botId.startsWith('bot_')) {
        botId = botId.substring(4);
      }

      const chatIDForDelete = botId ? localStorage.getItem(`chatId_${botId}`) : null;

      if (apiKey && attachment?.id) {
        await axios.delete(`${mediaApiURL}/files/${attachment.id}`, {
          headers: {
            'Api-token': apiKey,
            ...(chatIDForDelete ? { 'X-HMAC-Key': chatIDForDelete } : {}),
            ...(orgId ? { 'X-ORG-ID': orgId } : {}),
          },
        });
      }
    } catch (error) {
      console.error('Error deleting file from server:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Renders preview for non-image files
   * @param {Object} attachment - Attachment object
   * @returns {JSX.Element} Preview component
   */
  const renderNonImagePreview = (attachment) => (
    <div className="flex flex-col items-center justify-center h-full w-full">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      </svg>
    </div>
  );

  if (!attachments?.length) {
    return (
      <input
        type="file"
        multiple
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileSelection}
        disabled={attachments?.length >= maxAttachments}
        accept={convertToBrowserAcceptTypes(allowedFileTypes)}
      />
    );
  }

  return (
    <>
      <div className="attachments-scroll relative flex flex-nowrap gap-3 overflow-x-auto py-1 w-full max-w-full">
        <Loader primary={primary} showLoader={isUploadingFiles || isDeleting} inline={true} size={20} />
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className={`relative flex ${attachment.isImage ? 'p-0' : 'min-w-[170px] p-1'
              } items-center gap-2 rounded-2xl border border-[#ececec] bg-[#f7f7fb] dark:border-white/10 dark:bg-[#1d1d21]`}
          >
            <button
              className="absolute -top-1 -right-2 h-5 w-5 rounded-full bg-black text-white font-bold shadow-md border dark:bg-[#111113] dark:text-white dark:border-white/10 flex items-center justify-center"
              onClick={async () => {
                if (typeof onAttachmentRemove === 'function') {
                  await deleteFileFromServer(attachment);
                  revokeAttachmentPreview(attachment);
                  onAttachmentRemove(attachment.id);
                }
              }}
              type="button"
              aria-label={t('Remove file')}
              disabled={isUploadingFiles}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 1L1 13" stroke="currentColor" strokeWidth="2" />
                <path d="M1 1L13 13" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            <div
              className={`rounded-xl border border-[#e4e4eb] bg-white dark:border-white/10 dark:bg-[#141418] overflow-hidden flex items-center justify-center ${attachment.isImage ? 'h-[70px] w-[70px] cursor-pointer' : 'h-12 w-12'
                }`}
            >
              {attachment.isImage && attachment.previewUrl ? (
                <img
                  src={attachment.previewUrl}
                  alt={attachment.name}
                  className="h-full w-full object-cover"
                  onClick={() => setPreviewedImage(attachment)}
                />
              ) : (
                renderNonImagePreview(attachment)
              )}
            </div>
            {!attachment.isImage && (
              <div className="flex flex-col min-w-0">
                <p className="mb-0 text-[13px] font-medium text-[#151521] dark:text-white truncate">
                  {attachment.name}
                </p>
                <span className="text-[11px] mt-1 text-gray-600 dark:text-gray-200">{attachment?.extension}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <input
        type="file"
        multiple
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileSelection}
        disabled={attachments?.length >= maxAttachments || isUploadingFiles}
        accept={convertToBrowserAcceptTypes(allowedFileTypes)}
      />

      <ImagePreview
        isOpen={!!previewedImage && !!previewedImage.previewUrl}
        imageSrc={previewedImage?.previewUrl}
        imageAlt={previewedImage?.name || 'Image preview'}
        onClose={() => setPreviewedImage(null)}
      />
    </>
  );
};

export default FileAttachments;
export { formatFileSize, MAX_ATTACHMENTS, MAX_FILE_SIZE_MB, MAX_FILE_SIZE_BYTES };